import { CheckOutlined } from '@ant-design/icons'
import { Button, Form, Input, Select, Switch, Tooltip } from 'antd'
import { FaqArticlePayload, FaqCategoryItem } from 'Modules'
import React, { useEffect, useMemo } from 'react'

import RichTextEditor from 'component/rich-text-editor/rich-text-editor'
import { isRichTextEmpty, trimRichTextHtml } from 'component/rich-text-editor/utils'
import { MAX_FAQ_ARTICLES_PER_CATEGORY } from 'constants/faq'
import { useFaqCategoryList } from 'hooks/faq'

import style from './faq-article-form.module.scss'
import layout from './faq-article-layout.module.scss'

const TITLE_MAX = 200

export interface FaqArticleFormValues {
  title: string
  content: string
  categoryId?: string
  published: boolean
}

const EMPTY_VALUES: FaqArticleFormValues = {
  title: '',
  content: '',
  categoryId: undefined,
  published: false,
}

interface CategoryOption {
  value: string
  label: string
  category: FaqCategoryItem
}

interface FaqArticleFormProps {
  initialValues?: FaqArticleFormValues
  submitting?: boolean
  onSubmit: (payload: FaqArticlePayload) => void
  onCancel: () => void
  onDirtyChange?: (dirty: boolean) => void
}

export const FaqArticleForm: React.FC<FaqArticleFormProps> = ({
  initialValues = EMPTY_VALUES,
  submitting,
  onSubmit,
  onCancel,
  onDirtyChange,
}) => {
  const [form] = Form.useForm<FaqArticleFormValues>()
  const { items: categories, isLoading: categoriesLoading } = useFaqCategoryList()

  const title: string = Form.useWatch('title', form) ?? initialValues.title
  const content: string = Form.useWatch('content', form) ?? initialValues.content
  const categoryId: string | undefined = Form.useWatch('categoryId', form)
  const published: boolean = Form.useWatch('published', form) ?? initialValues.published

  // Full categories are hidden, except the article's current one (edit mode)
  const categoryOptions = useMemo<CategoryOption[]>(
    () =>
      categories
        .filter(
          (category) =>
            category.articleCount < MAX_FAQ_ARTICLES_PER_CATEGORY ||
            category._id === initialValues.categoryId
        )
        .map((category) => ({ value: category._id, label: category.name, category })),
    [categories, initialValues.categoryId]
  )

  const categoryAvailable = !!categoryId && categoryOptions.some((o) => o.value === categoryId)

  // Drop a selection whose category became full after a refetch
  useEffect(() => {
    if (categoryId && !categoriesLoading && !categoryAvailable) {
      form.setFieldValue('categoryId', undefined)
    }
  }, [categoryId, categoriesLoading, categoryAvailable, form])

  const canSubmit = !!title.trim() && !isRichTextEmpty(content) && categoryAvailable

  const dirty =
    title !== initialValues.title ||
    trimRichTextHtml(content) !== trimRichTextHtml(initialValues.content) ||
    categoryId !== initialValues.categoryId ||
    published !== initialValues.published

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  const handleSave = async () => {
    let values: FaqArticleFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    onSubmit({
      title: values.title.trim(),
      content: trimRichTextHtml(values.content),
      categoryId: values.categoryId as string,
      status: values.published ? 'PUBLISHED' : 'UNPUBLISHED',
    })
  }

  const renderCategoryOption = (category: FaqCategoryItem) => (
    <div className={style.categoryOption}>
      <span className={style.categoryOptionName} title={category.name}>
        {category.name}
      </span>
      <span className={style.categoryOptionCount}>
        ({category.articleCount}/{MAX_FAQ_ARTICLES_PER_CATEGORY})
      </span>
      {category.visibility === 'HIDDEN' ? <span className={layout.hiddenTag}>Hidden</span> : null}
      {category._id === categoryId ? <CheckOutlined className={style.categoryOptionCheck} /> : null}
    </div>
  )

  return (
    <Form form={form} layout="vertical" initialValues={initialValues} className={style.form}>
      <div className={layout.columns}>
        <section className={layout.card}>
          <Form.Item
            name="title"
            label={
              <span className={style.labelRow}>
                Title
                <span className={style.labelCounter}>
                  {title.length}/{TITLE_MAX}
                </span>
              </span>
            }
            className={style.titleItem}
            validateTrigger={['onChange', 'onBlur']}
            rules={[{ required: true, whitespace: true, message: 'Title is required' }]}
          >
            <Input placeholder="Enter article title" maxLength={TITLE_MAX} />
          </Form.Item>
          <Form.Item
            name="content"
            label="Content"
            required
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                validator: (_, value?: string) =>
                  isRichTextEmpty(value)
                    ? Promise.reject(new Error('Content is required'))
                    : Promise.resolve(),
              },
            ]}
            className={style.contentItem}
          >
            <RichTextEditor placeholder="Write the content of this article…" minHeight={360} />
          </Form.Item>
        </section>

        <aside className={layout.card}>
          <div className={layout.cardTitle}>Settings</div>
          <Form.Item
            name="categoryId"
            label="Category"
            validateTrigger={['onChange', 'onBlur']}
            rules={[{ required: true, message: 'Category is required' }]}
          >
            <Select<string, CategoryOption>
              placeholder="Select category"
              loading={categoriesLoading}
              options={categoryOptions}
              optionRender={(option) => renderCategoryOption(option.data.category)}
              popupClassName={style.categoryPopup}
              onDropdownVisibleChange={(open) => {
                if (!open) {
                  form.validateFields(['categoryId']).catch(() => undefined)
                }
              }}
            />
          </Form.Item>
          <Form.Item label="Status" className={style.statusItem}>
            <div className={style.statusRow}>
              <Form.Item name="published" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
              <span>{published ? 'Published' : 'Unpublished'}</span>
            </div>
          </Form.Item>
        </aside>
      </div>

      <div className={style.footer}>
        <Button onClick={onCancel}>Cancel</Button>
        <Tooltip title={canSubmit ? '' : 'Fill in all required fields'}>
          <span className={canSubmit ? undefined : style.disabledWrap}>
            <Button
              type="primary"
              disabled={!canSubmit}
              loading={submitting}
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </span>
        </Tooltip>
      </div>
    </Form>
  )
}
